import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

/**
 * Validate trades for a trigger against exchange APIs
 * The USD/KRW rate is fetched from the backend (Redis)
 * @param {Object} params - { triggerUuid }
 */
export const validateTrades = createAsyncThunk(
  'validation/validateTrades',
  async ({ triggerUuid }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/api/triggers/${triggerUuid}/validate_trades/`, {});
      return response.data;
    } catch (error) {
      // Check if rate is not available
      if (error.response?.data?.rate_available === false) {
        return rejectWithValue('USD/KRW exchange rate is not available. Please try again later.');
      }
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Validation failed'
      );
    }
  }
);

const initialState = {
  // Validation state
  validating: false,
  validationResult: null,
  validationError: null,
  validationProgress: null, // For progress tracking

  // Modal state
  validationDialogOpen: false,
  selectedTriggerForValidation: null,
};

const validationSlice = createSlice({
  name: 'validation',
  initialState,
  reducers: {
    openValidationDialog: (state, action) => {
      state.validationDialogOpen = true;
      state.selectedTriggerForValidation = action.payload;
      state.validationResult = null;
      state.validationError = null;
    },
    closeValidationDialog: (state) => {
      state.validationDialogOpen = false;
      state.selectedTriggerForValidation = null;
      state.validationResult = null;
      state.validationError = null;
    },
    clearValidationResult: (state) => {
      state.validationResult = null;
      state.validationError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateTrades.pending, (state) => {
        state.validating = true;
        state.validationError = null;
      })
      .addCase(validateTrades.fulfilled, (state, action) => {
        state.validating = false;
        state.validationResult = action.payload;
        state.validationError = null;
      })
      .addCase(validateTrades.rejected, (state, action) => {
        state.validating = false;
        state.validationError = action.payload || 'Validation failed';
      });
  },
});

export const {
  openValidationDialog,
  closeValidationDialog,
  clearValidationResult,
} = validationSlice.actions;

export default validationSlice.reducer;
